import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

import Loading from "../common/loading";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
);

const PRESET_RANGES = [
  { key: "1h", label: "Last hour", durationMs: 60 * 60 * 1000 },
  { key: "6h", label: "Last 6 hours", durationMs: 6 * 60 * 60 * 1000 },
  { key: "24h", label: "Last 24 hours", durationMs: 24 * 60 * 60 * 1000 },
  { key: "7d", label: "Last 7 days", durationMs: 7 * 24 * 60 * 60 * 1000 },
  { key: "30d", label: "Last 30 days", durationMs: 30 * 24 * 60 * 60 * 1000 },
  { key: "custom", label: "Custom range" },
];

const RESOURCE_QUERY_PARAM_BY_LOG_TYPE = {
  outlets: "outlet_id",
  inlets: "inlet_id",
  sensors: "sensor_id",
};

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 3,
});

function createRangeFromDuration(durationMs, anchor = Date.now()) {
  const before = anchor;
  const since = Math.max(0, before - durationMs);
  return { since, before };
}

function formatRange(sinceMs, beforeMs) {
  try {
    const start = format(new Date(sinceMs), "MMM d, yyyy p");
    const end = format(new Date(beforeMs), "MMM d, yyyy p");
    return `${start} – ${end}`;
  } catch (err) {
    return "";
  }
}

function computeBucketMinutes(durationMs) {
  if (!durationMs || durationMs <= 0) return 0;
  const minutes = durationMs / 60000;
  const approx = Math.max(1, Math.round(minutes / 300));
  return Math.min(24 * 60, approx);
}

const TIME_DISPLAY_FORMATS = {
  minute: "MMM d, h:mm a",
  hour: "MMM d, h a",
  day: "MMM d",
  week: "MMM d",
  month: "MMM yyyy",
  year: "yyyy",
};

function resolveTimeUnit(durationMs) {
  const minutes = durationMs / 60000;
  if (!Number.isFinite(minutes) || minutes <= 0) return "hour";
  if (minutes <= 180) return "minute"; // up to 3 hours
  if (minutes <= 60 * 24 * 14) return "hour"; // up to 2 weeks
  if (minutes <= 60 * 24 * 120) return "day"; // up to ~4 months
  if (minutes <= 60 * 24 * 365 * 2) return "month"; // up to 2 years
  return "year";
}

function withAlpha(color, alpha) {
  if (!color) return `rgba(37, 99, 235, ${alpha})`;
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex;
    if (normalized.length === 6) {
      const value = parseInt(normalized, 16);
      const r = (value >> 16) & 255;
      const g = (value >> 8) & 255;
      const b = value & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  return color;
}

export default function DeviceLogChart({
  title,
  subtitle,
  logType,
  enclosureSerialNumber,
  resourceId,
  metricKey,
  metricLabel,
  unit,
  color = "#2563eb",
  lineLabel,
  controls,
  emptyMessage = "No log data for the selected range.",
}) {
  const defaultPreset = PRESET_RANGES.find((p) => p.key === "24h" && p.durationMs);
  const defaultRange = defaultPreset
    ? createRangeFromDuration(defaultPreset.durationMs)
    : createRangeFromDuration(24 * 60 * 60 * 1000);

  const [timeRangeKey, setTimeRangeKey] = useState("24h");
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [customRange, setCustomRange] = useState([
    new Date(defaultRange.since),
    new Date(defaultRange.before),
  ]);
  const [customError, setCustomError] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    setCustomRange([new Date(appliedRange.since), new Date(appliedRange.before)]);
  }, [appliedRange.since, appliedRange.before]);

  useEffect(() => {
    if (!enclosureSerialNumber || resourceId === undefined || resourceId === null || !metricKey) {
      setRows([]);
      return;
    }

    const duration = appliedRange.before - appliedRange.since;
    if (duration <= 0) {
      setError("Invalid time range selected.");
      setRows([]);
      return;
    }

    setLoading(true);
    setError("");
    let isActive = true;
    const controller = new AbortController();
    const params = new URLSearchParams();
    params.set("enclosureSerialNumber", String(enclosureSerialNumber));
    params.set("since", String(Math.floor(appliedRange.since)));
    params.set("before", String(Math.floor(appliedRange.before)));

    const resourceParam = RESOURCE_QUERY_PARAM_BY_LOG_TYPE[logType];
    if (!resourceParam) {
      setError("Unsupported log type.");
      setRows([]);
      setLoading(false);
      return undefined;
    }
    params.set(resourceParam, String(resourceId));

    const bucketMinutes = computeBucketMinutes(duration);
    if (bucketMinutes > 0) {
      params.set("bucketMinutes", String(bucketMinutes));
    }

    axios
      .get(`/v1/logs/${logType}?${params.toString()}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        signal: controller.signal,
      })
      .then((res) => {
        if (!isActive) return;
        const data = res.data?.data || [];
        const sanitized = data
          .map((row) => {
            const createdAt = row.createdAt ? new Date(row.createdAt) : null;
            if (!createdAt || Number.isNaN(createdAt.getTime())) return null;
            return { ...row, createdAt };
          })
          .filter(Boolean)
          .sort((a, b) => a.createdAt - b.createdAt);
        setRows(sanitized);
      })
      .catch((err) => {
        if (!isActive) return;
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
        console.error(err);
        setError("Unable to load log data. Please try again.");
        setRows([]);
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [
    logType,
    enclosureSerialNumber,
    resourceId,
    metricKey,
    appliedRange.since,
    appliedRange.before,
    refreshIndex,
  ]);

  const datasetLabel = lineLabel || metricLabel;

  const chartData = useMemo(() => {
    const points = rows.map((row) => {
      const rawVal = row[metricKey];
      if (rawVal === null || rawVal === undefined) {
        return { x: row.createdAt, y: null };
      }
      const numeric = Number(rawVal);
      return {
        x: row.createdAt,
        y: Number.isFinite(numeric) ? numeric : null,
      };
    });

    return {
      datasets: [
        {
          label: datasetLabel,
          data: points,
          fill: true,
          borderColor: color,
          backgroundColor: withAlpha(color, 0.18),
          pointRadius: 0,
          borderWidth: 2,
          tension: 0.18,
          spanGaps: false,
          parsing: false,
        },
      ],
    };
  }, [rows, metricKey, datasetLabel, color]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "nearest", intersect: false },
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed?.y;
              if (value === null || value === undefined) {
                return `${datasetLabel}: no data`;
              }
              const formatted = numberFormatter.format(value);
              return unit ? `${datasetLabel}: ${formatted} ${unit}` : `${datasetLabel}: ${formatted}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "time",
          bounds: "data",
          min: appliedRange.since,
          max: appliedRange.before,
          time: {
            tooltipFormat: "PP p",
            unit: resolveTimeUnit(appliedRange.before - appliedRange.since),
            displayFormats: TIME_DISPLAY_FORMATS,
          },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            source: "auto",
          },
          grid: { display: false },
        },
        y: {
          ticks: {
            callback: (val) => {
              const formatted = numberFormatter.format(val);
              return unit ? `${formatted} ${unit}` : formatted;
            },
          },
          title: unit
            ? {
                display: true,
                text: unit,
                font: { size: 12 },
              }
            : undefined,
        },
      },
    }),
    [datasetLabel, unit, appliedRange.since, appliedRange.before],
  );

  const rangeText = useMemo(
    () => formatRange(appliedRange.since, appliedRange.before),
    [appliedRange.since, appliedRange.before],
  );

  const selectStyle = {
    margin: 0,
    minWidth: "150px",
    padding: "0.35rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #d7dde2",
    backgroundColor: "#fff",
  };

  const secondaryButtonStyle = {
    backgroundColor: "#f5f7fa",
    color: "#374151",
    border: "1px solid #d7dde2",
    padding: "0.35rem 0.75rem",
    borderRadius: "4px",
    fontSize: "0.85rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
  };

  const containerStyle = {
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  };

  const controlHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
  };

  const controlHeadingStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
  };

  const controlActionsStyle = {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  };

  const controlGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
  };

  const inlineLabelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    fontSize: "0.85rem",
    color: "#374151",
    fontWeight: 500,
  };

  const customRangeWrapperStyle = {
    display: "flex",
    justifyContent: "flex-end",
  };

  const customRangeInnerStyle = {
    width: "100%",
    maxWidth: "520px",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  };

  const customDateInputsRowStyle = {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  };

  const customDateLabelStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    color: "#4b5563",
    fontSize: "0.8rem",
    minWidth: "220px",
    flex: "1 1 220px",
  };

  const chartHeaderStyle = {
    // marginBottom: "0.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
  };

  const chartTitleStyle = {
    fontWeight: 600,
    fontSize: "1.05rem",
  };

  const chartSubtitleStyle = {
    color: "#4b5563",
    fontSize: "0.9rem",
  };

  const chartRangeStyle = {
    color: "#6b7280",
    fontSize: "0.8rem",
  };

  const chartWrapperStyle = {
    minHeight: "260px",
    position: "relative",
  };

  const handlePresetChange = (event) => {
    const key = event.target.value;
    setTimeRangeKey(key);
    setCustomError("");

    if (key === "custom") {
      setCustomRange([new Date(appliedRange.since), new Date(appliedRange.before)]);
      return;
    }

    const preset = PRESET_RANGES.find((p) => p.key === key && p.durationMs);
    if (preset?.durationMs) {
      const range = createRangeFromDuration(preset.durationMs);
      setAppliedRange(range);
    }
  };

  const handleCustomStartChange = (startDate) => {
    setCustomRange((prev) => [startDate || null, prev?.[1] || null]);
    setCustomError("");
  };

  const handleCustomEndChange = (endDate) => {
    setCustomRange((prev) => [prev?.[0] || null, endDate || null]);
    setCustomError("");
  };

  const handleApplyCustom = () => {
    const [start, end] = customRange;
    if (!start || !end) {
      setCustomError("Select a start and end time.");
      return;
    }
    if (start.getTime() >= end.getTime()) {
      setCustomError("Start time must be before end time.");
      return;
    }
    setAppliedRange({ since: start.getTime(), before: end.getTime() });
    setCustomError("");
  };

  const handleResetCustom = () => {
    setCustomRange([new Date(appliedRange.since), new Date(appliedRange.before)]);
    setCustomError("");
  };

  const handleRefresh = () => {
    if (timeRangeKey === "custom") {
      setRefreshIndex((idx) => idx + 1);
      return;
    }
    const preset = PRESET_RANGES.find((p) => p.key === timeRangeKey && p.durationMs);
    const duration = preset?.durationMs || (appliedRange.before - appliedRange.since);
    const newRange = createRangeFromDuration(duration, Date.now());
    setAppliedRange(newRange);
  };

  const disableApply = !customRange[0] || !customRange[1];

  return (
    <div style={containerStyle}>
      <div className="app-card">
        <div style={controlHeaderStyle}>
          <div style={controlHeadingStyle}>
            <div style={chartHeaderStyle}>
              <div style={chartTitleStyle}>{title}</div>
              {subtitle && <div style={chartSubtitleStyle}>{subtitle}</div>}
              <div style={chartRangeStyle}>{rangeText}</div>
            </div>
          </div>
          <div style={controlActionsStyle}>
            {controls ? <div style={controlGroupStyle}>{controls}</div> : null}
            <div style={controlGroupStyle}>
              <label style={inlineLabelStyle}>
                <span>Timeframe</span>
                <select value={timeRangeKey} onChange={handlePresetChange} style={selectStyle}>
                  {PRESET_RANGES.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" onClick={handleRefresh} style={secondaryButtonStyle}>
                <i className="material-icons" style={{ fontSize: "18px" }}>
                  refresh
                </i>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {timeRangeKey === "custom" && (
          <div style={customRangeWrapperStyle}>
            <div style={customRangeInnerStyle}>
              <div style={customDateInputsRowStyle}>
                <label style={customDateLabelStyle}>
                  <span style={{ fontWeight: 600 }}>Start</span>
                  <DatePicker
                    selected={customRange[0]}
                    onChange={handleCustomStartChange}
                    selectsStart
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                    showTimeSelect
                    dateFormat="Pp"
                    maxDate={customRange[1] || new Date()}
                    className="device-log-chart__datepicker"
                  />
                </label>
                <label style={customDateLabelStyle}>
                  <span style={{ fontWeight: 600 }}>End</span>
                  <DatePicker
                    selected={customRange[1]}
                    onChange={handleCustomEndChange}
                    selectsEnd
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                    minDate={customRange[0] || null}
                    showTimeSelect
                    dateFormat="Pp"
                    maxDate={new Date()}
                    className="device-log-chart__datepicker"
                  />
                </label>
              </div>
              {customError && (
                <div style={{ color: "#b91c1c", fontSize: "0.8rem" }}>{customError}</div>
              )}
              <div className="custom-range-actions">
                <button
                  type="button"
                  onClick={handleResetCustom}
                  className="custom-range-action-btn custom-range-action-btn--reset"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleApplyCustom}
                  className="custom-range-action-btn custom-range-action-btn--apply"
                  disabled={disableApply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="app-card__section" style={{ paddingTop: "0" }}>
        <div style={chartWrapperStyle}>
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <Loading />
            </div>
          )}
          <Line options={chartOptions} data={chartData} />
          {!loading && !error && rows.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6b7280",
                fontSize: "0.95rem",
              }}
            >
              {emptyMessage}
            </div>
          )}
        </div>

        {error && (
          <div style={{ color: "#b91c1c", fontSize: "0.85rem", marginTop: "0.75rem" }}>{error}</div>
        )}
        </div>
      </div>
    </div>
  );
}
